import { useState, useRef, useEffect, useCallback } from 'react';
import _ from 'underscore';

type ReturnProps = [React.MutableRefObject<HTMLDivElement>, boolean];

interface UseClickOutsideProps {
  ignoreModal?: boolean;
  ignoreDropdownMenu?: boolean;
  ignoreIds?: string[];
  ignoreClassNames?: string[];
}

const useClickOutside = ({
  ignoreModal,
  ignoreDropdownMenu,
  ignoreIds,
  ignoreClassNames,
}: UseClickOutsideProps = {}): ReturnProps => {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({
    hasClickedOutside: false,
  });

  const handleEvent = useCallback(
    (e) => {
      if (ref && ref.current) {
        if (ref.current.contains(e.target)) {
          setState({ hasClickedOutside: false });
          return;
        } else {
          if (ignoreIds?.length) {
            const match = _.find(ignoreIds, (ignoreId) =>
              document.getElementById(ignoreId)?.contains(e.target),
            );
            if (match) {
              setState({ hasClickedOutside: false });
              return;
            }
          }

          if (ignoreClassNames?.length) {
            const match = _.find(ignoreClassNames, (cn) => {
              return _.find(document.querySelectorAll(`.${cn}`), (item) => item.contains(e.target));
            });
            if (match) {
              setState({ hasClickedOutside: false });
              return;
            }
          }

          let topModal;
          if (ignoreModal) {
            let topModalZIndex;
            document.querySelectorAll('.modal').forEach((item) => {
              if (item.classList.contains('drawer__wrapper')) return;

              const zIndex = +window.getComputedStyle(item).zIndex;
              if (!topModalZIndex || zIndex > topModalZIndex) {
                topModal = item;
                topModalZIndex = zIndex;
              }
            });

            let inDropdown;
            if (ignoreDropdownMenu) {
              document.querySelectorAll('.dropdown-menu--in-portal').forEach((item) => {
                if (item.contains(e.target)) {
                  inDropdown = true;
                }
              });
            }

            setState({
              hasClickedOutside: (!topModal || !topModal.contains(e.target)) && !inDropdown,
            });
            return;
          } else {
            setState({ hasClickedOutside: true });
          }
        }
      }
    },
    [ignoreDropdownMenu, ignoreIds, ignoreClassNames, ignoreModal],
  );

  useEffect(() => {
    if (window.PointerEvent) {
      document.addEventListener('pointerdown', handleEvent);
    } else {
      document.addEventListener('mousedown', handleEvent);
      document.addEventListener('touchstart', handleEvent);
    }

    return () => {
      if (window.PointerEvent) {
        document.removeEventListener('pointerdown', handleEvent);
      } else {
        document.removeEventListener('mousedown', handleEvent);
        document.removeEventListener('touchstart', handleEvent);
      }
    };
  }, [handleEvent]);

  return [ref, state.hasClickedOutside];
};

export { useClickOutside };
